import { createBrowserHistory } from 'history';
import { renderRoot } from 'quartz-core/client';
import historyAdapter from 'react-rhumb/historyAdapterV4';
import rhumbConfig from '../rhumb-config.yaml';

// Redux
import { Provider } from 'react-redux';
import { startApp } from './actions/AppActions';
import { getAuth } from './auth/actions/AuthActions';
import { configureStore } from './utils/configureStore';

// Configure the router
import { EXTENSION_UIIFRAME, NAV, UIIFRAME } from 'chatspot-core/types/LayoutVariants';
import CopilotChannelProvider from 'copilot-toolkit/channel/CopilotChannelProvider';
import { parse } from 'hub-http/helpers/params';
import { chatspotSidebarComponentDef } from 'navigation-components/config';
import { NavigationComponent } from 'navigation-components/consumer/components';
import { Router } from 'react-router-dom';
import GlobalContext from './GlobalContext';
import NavigationVariantRhumbProvider from './NavigationVariantRhumbProvider';
import { selectGates } from './auth/selectors/authSelectors';
import { getAiSettings } from './chat/actions/getAiSettings';
import { fetchChatspotAuthInfo } from './chatspotAuth/slices/chatspotAuth';
import Layout from './components/Layout';
import UIIFrameVariantAppContainer from './iframe/UIIFrameVariantAppContainer';
import NavigationComponentSetup from './nav/NavigationComponentSetup';
import { getRootUrl } from './utils/urls';
import { usageTracker } from './utils/usageTracker';

// Ably
import AblyRealtimeClientProvider from './ably/context/AblyRealtimeClientProvider';

// Apollo & DFC
import { ApolloProvider } from '@apollo/client';
import { DataFetchingClient, DataFetchingClientProvider } from 'data-fetching-client';
import { RhumbProvider } from 'react-rhumb';
import { apolloClient } from './graphQL/client';
import { jsx as _jsx } from "react/jsx-runtime";
const fetchingClient = new DataFetchingClient();
export class App {
  constructor() {
    this.store = configureStore();
  }
  // Kicks off the app. This is intentionally not in the constructor.
  async start() {
    await this.startAuth();
    this.store.dispatch(startApp());
    this.startRouter();
  }
  async startAuth() {
    const auth = await this.store.dispatch(getAuth()).catch(error => {
      throw new Error(error.message);
    });
    if (auth.user.scopes.includes('chatspot-access')) {
      // Fetch chatspot portal settings if the user has chatspot-access scope
      await this.store.dispatch(fetchChatspotAuthInfo()).unwrap().catch(error => {
        throw error;
      });
      this.store.dispatch(getAiSettings()).unwrap().catch(error => {
        throw error;
      });
    }
    return auth;
  }

  // Temporarily break up rendered components until react-rhumb supports programmatic latency metrics: Slack => https://hubspot.slack.com/archives/C6S4FFRLM/p1747145209018109?thread_ts=1745270304.668899&cid=C6S4FFRLM
  renderNavigationVariant({
    history
  }) {
    return /*#__PURE__*/_jsx(NavigationComponent.Provider, {
      definition: chatspotSidebarComponentDef,
      definitionParams: {
        gates: selectGates(this.store.getState())
      },
      children: /*#__PURE__*/_jsx(NavigationVariantRhumbProvider
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      , {
        config: rhumbConfig,
        history: historyAdapter(history),
        children: /*#__PURE__*/_jsx(Router, {
          history: history,
          children: /*#__PURE__*/_jsx(NavigationComponent.Renderer, {
            component: NavigationComponentSetup,
            definition: chatspotSidebarComponentDef,
            definitionParams: {
              gates: selectGates(this.store.getState())
            }
          })
        })
      })
    });
  }

  // Temporarily break up rendered components until react-rhumb supports programmatic latency metrics: Slack => https://hubspot.slack.com/archives/C6S4FFRLM/p1747145209018109?thread_ts=1745270304.668899&cid=C6S4FFRLM
  renderUIIframeVariant({
    history,
    hostAppName,
    hostAppPathName,
    startOpen
  }) {
    return /*#__PURE__*/_jsx(RhumbProvider
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    , {
      config: rhumbConfig,
      history: historyAdapter(history),
      children: /*#__PURE__*/_jsx(Router, {
        history: history,
        children: /*#__PURE__*/_jsx(UIIFrameVariantAppContainer, {
          hostAppName: hostAppName,
          hostAppPathName: hostAppPathName,
          startOpen: startOpen
        })
      })
    });
  }
  startRouter() {
    const history = createBrowserHistory({
      basename: getRootUrl()
    });
    let hostAppName = 'unknown';
    let hostAppPathName = 'unknown';
    let startOpen = false;
    let variant = NAV;
    let customCopilotId;
    let isPreviewMode = false;
    try {
      const {
        hostAppName: hostAppNameFromQueryParam,
        pathname,
        startOpen: startOpenFromQueryParam,
        variant: variantFromQueryParam,
        customCopilotId: customCopilotIdFromQueryParam,
        isPreviewMode: isPreviewModeFromQueryParam
      } = parse(window.location.search.slice(1));
      if (startOpenFromQueryParam === 'true') {
        startOpen = true;
      }
      if (pathname) {
        hostAppPathName = decodeURIComponent(pathname);
      }
      if (customCopilotIdFromQueryParam) {
        const parsedId = parseInt(customCopilotIdFromQueryParam, 10);
        if (!isNaN(parsedId)) {
          customCopilotId = parsedId;
        }
      }
      if (isPreviewModeFromQueryParam === 'true') {
        isPreviewMode = true;
      }
      try {
        var _topWindow;
        let topWindow = window;
        while (topWindow.parent !== topWindow) {
          topWindow = topWindow.parent;
        }
        hostAppName = (_topWindow = topWindow) === null || _topWindow === void 0 || (_topWindow = _topWindow.hubspot) === null || _topWindow === void 0 || (_topWindow = _topWindow.bender) === null || _topWindow === void 0 ? void 0 : _topWindow.currentProject;
      } catch (e) {
        // Fallback to the query param if the top window is not a HubSpot window
        // AKA: Extension
        if (hostAppNameFromQueryParam) {
          hostAppName = hostAppNameFromQueryParam;
        }
      }
      if (variantFromQueryParam && (variantFromQueryParam === NAV || variantFromQueryParam === UIIFRAME || variantFromQueryParam === EXTENSION_UIIFRAME)) {
        variant = variantFromQueryParam;
      }
      usageTracker.setProperties({
        hostAppName,
        hasHub: 'is_hubspot_user',
        isHubful: true
      });
    } catch (e) {
      // Just swallow the error here if something happens, we can start without
      // it
    }
    usageTracker.setProperties({
      hostAppName,
      hasHub: 'is_hubspot_user',
      isHubful: true
    });
    renderRoot( /*#__PURE__*/_jsx(GlobalContext, {
      hostAppName: hostAppName,
      hostAppPathName: hostAppPathName,
      variant: variant,
      customCopilotId: customCopilotId,
      isPreviewMode: isPreviewMode,
      children: /*#__PURE__*/_jsx(Layout, {
        children: /*#__PURE__*/_jsx(CopilotChannelProvider, {
          children: /*#__PURE__*/_jsx(Provider, {
            store: this.store,
            children: /*#__PURE__*/_jsx(ApolloProvider, {
              client: apolloClient,
              children: /*#__PURE__*/_jsx(DataFetchingClientProvider, {
                client: fetchingClient,
                children: /*#__PURE__*/_jsx(AblyRealtimeClientProvider, {
                  children: variant === NAV ? this.renderNavigationVariant({
                    history
                  }) : this.renderUIIframeVariant({
                    history,
                    hostAppName,
                    hostAppPathName,
                    startOpen
                  })
                })
              })
            })
          })
        })
      })
    }));
  }
}